import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { guid } from "@buildwithflux/shared";
import { box2, vec2 } from "../../common";
import { DEFAULT_SYMBOL_NAME, ElementHelper } from "../../elements";
import { fetchDatasheetFromLcscProductPageUrl } from "../LCSC/fetchDatasheetFromLcscProductPageUrl";
import { SymbolToSvgConverter } from "./SymbolToSvgConverter";
/**
 * Computes the flip and orientation values for a terminal element based on pin direction.
 * This ensures terminal labels render on the outside of the symbol consistently
 * across all importers (KiCad, Eagle, Altium).
 *
 * - Left-facing pins: flip = true (labels render on the left/outside)
 * - Right-facing pins: flip = false (labels already on right/outside)
 * - Up-facing pins: rotate 90 degrees (labels render above)
 * - Down-facing pins: rotate -90 degrees (labels render below)
 *
 * @param direction - The direction the pin is facing (left, right, up, down)
 * @returns Position modifiers to apply to the terminal element's diagramPosition
 */
export function getTerminalPositionModifiers(direction) {
    switch (direction) {
        case "left":
            return { flip: true };
        case "right":
            return { flip: false };
        case "up":
            // Rotate 90 degrees counter-clockwise so labels render above
            return { flip: false, orientation: Math.PI / 2 };
        case "down":
            // Rotate 90 degrees clockwise so labels render below
            return { flip: false, orientation: -Math.PI / 2 };
    }
}
/**
 * Constructs a description for an imported part from available metadata.
 * Used by all importers (KiCad, Eagle, Altium) to ensure consistent behavior.
 *
 * Priority:
 * 1. Use the provided description if it's non-empty
 * 2. Construct from "Manufacturer MPN" if both are available
 * 3. Use manufacturer alone if available
 * 4. Use MPN alone if available
 * 5. Return empty string (not the default placeholder)
 *
 * @param description - The original description from the import source
 * @param manufacturer - The manufacturer name if available
 * @param mpn - The manufacturer part number if available
 * @returns A description string, or empty string if no useful info is available
 */
export function buildImportDescription(description, manufacturer, mpn) {
    // Use the provided description if it's non-empty
    if (description?.trim()) {
        return description.trim();
    }
    // Construct from manufacturer and MPN
    if (manufacturer?.trim() && mpn?.trim()) {
        return `${manufacturer.trim()} ${mpn.trim()}`;
    }
    // Use manufacturer alone
    if (manufacturer?.trim()) {
        return manufacturer.trim();
    }
    // Use MPN alone
    if (mpn?.trim()) {
        return mpn.trim();
    }
    // Return empty string to avoid showing the default placeholder
    return "";
}
/** Minimum length for a valid part name (matches isDocumentNameValid requirement) */
const MIN_PART_NAME_LENGTH = 3;
/**
 * Ensures a part name is valid for import by expanding short names.
 * Document names must be at least 3 characters to pass isDocumentNameValid().
 * Short names like "L", "C", "R" are expanded using MPN, manufacturer, or a prefix.
 *
 * Used by all importers (KiCad, Eagle, Altium) to ensure consistent behavior.
 *
 * @param name - The original part name from the import source
 * @param manufacturer - The manufacturer name if available
 * @param mpn - The manufacturer part number if available
 * @param prefix - Prefix to use as fallback (e.g., "Eagle", "KiCad", "Altium")
 * @returns A valid part name that is at least 3 characters long
 */
export function buildValidPartName(name, manufacturer, mpn, prefix) {
    // If name is already valid length, return it
    if (name && name.length >= MIN_PART_NAME_LENGTH) {
        return name;
    }
    // If no name at all, try to construct from MPN or manufacturer
    if (!name) {
        if (mpn?.trim()) {
            return mpn.trim();
        }
        if (manufacturer?.trim()) {
            return manufacturer.trim();
        }
        return `${prefix} Part`;
    }
    // Name exists but is too short - try to expand it
    // Try to use MPN to make a better name
    if (mpn?.trim()) {
        return mpn.trim();
    }
    // Try to use manufacturer to expand the name
    if (manufacturer?.trim()) {
        return `${manufacturer.trim()} ${name}`;
    }
    // Fallback: prefix with the importer name to make it valid
    return `${prefix} ${name}`;
}
/**
 * Builds standard document properties for imported parts.
 * Always includes the 5 standard properties (MPN, Manufacturer, Part Type, Datasheet URL, Designator Prefix)
 * even if values are empty, so users can easily add values later.
 * Also adds component-specific electrical properties (resistance, capacitance, inductance) based on part type.
 *
 * @param importData - The import data containing property values
 * @returns A map of property UIDs to property data
 */
export function buildProperties({ manufacturer, designator, datasheetUrl, mpn, lcscDpn, productDetailUrl, productImageUrl, packageCode, partType, value, jlcPcbPartClass, }) {
    let propertyOrder = 0;
    const properties = [];
    // Always add the 5 standard properties (even if empty) so users can easily fill them in
    // 1. Manufacturer Part Number (MPN)
    const mpnProperty = {
        uid: guid(),
        name: defaultPropertyDefinitions.mpn.label,
        value: mpn ?? "",
        order: propertyOrder++,
    };
    properties.push(mpnProperty);
    // 2. Manufacturer Name
    const manufacturerNameProperty = {
        uid: guid(),
        name: defaultPropertyDefinitions.manufacturer_name.label,
        value: manufacturer ?? "",
        order: propertyOrder++,
    };
    properties.push(manufacturerNameProperty);
    // 3. Part Type
    const partTypeProperty = {
        uid: guid(),
        name: defaultPropertyDefinitions.part_type.label,
        value: partType ?? "",
        order: propertyOrder++,
    };
    properties.push(partTypeProperty);
    // 4. Datasheet URL
    const datasheetUrlProperty = {
        uid: guid(),
        name: defaultPropertyDefinitions.datasheet_url.label,
        value: datasheetUrl ?? "",
        order: propertyOrder++,
    };
    properties.push(datasheetUrlProperty);
    // 5. Designator Prefix (default to "U" if not available from importer)
    const designatorPrefixProperty = {
        uid: guid(),
        name: defaultPropertyDefinitions.designator_prefix.label,
        value: designator ?? "U",
        order: propertyOrder++,
    };
    properties.push(designatorPrefixProperty);
    // Add component-specific electrical properties based on part type
    // These are always added for the relevant part types so users can easily fill them in
    if (partType) {
        const partTypeLower = partType.toLowerCase();
        if (partTypeLower.includes("resistor")) {
            const resistanceProperty = {
                uid: guid(),
                name: defaultPropertyDefinitions.resistance.label,
                value: value ? value.split(defaultPropertyDefinitions.resistance.unit ?? "")[0] : "",
                unit: defaultPropertyDefinitions.resistance.unit,
                order: propertyOrder++,
            };
            properties.push(resistanceProperty);
        }
        else if (partTypeLower.includes("capacitor")) {
            const capacitanceProperty = {
                uid: guid(),
                name: defaultPropertyDefinitions.capacitance.label,
                value: value ? value.split(defaultPropertyDefinitions.capacitance.unit ?? "")[0] : "",
                unit: defaultPropertyDefinitions.capacitance.unit,
                order: propertyOrder++,
            };
            properties.push(capacitanceProperty);
        }
        else if (partTypeLower.includes("inductor")) {
            const inductanceProperty = {
                uid: guid(),
                name: defaultPropertyDefinitions.inductance.label,
                value: value ? value.split(defaultPropertyDefinitions.inductance.unit ?? "")[0] : "",
                unit: defaultPropertyDefinitions.inductance.unit,
                order: propertyOrder++,
            };
            properties.push(inductanceProperty);
        }
    }
    // Add optional properties only if they have values
    if (lcscDpn) {
        const lcscDpnProperty = {
            uid: guid(),
            name: defaultPropertyDefinitions.lcsc_dpn.label,
            value: lcscDpn,
            order: propertyOrder++,
        };
        properties.push(lcscDpnProperty);
    }
    if (productDetailUrl) {
        const productDetailUrlProperty = {
            uid: guid(),
            name: defaultPropertyDefinitions.product_info_url.label,
            value: productDetailUrl,
            order: propertyOrder++,
        };
        properties.push(productDetailUrlProperty);
    }
    if (productImageUrl) {
        const productImageUrlProperty = {
            uid: guid(),
            name: defaultPropertyDefinitions.product_image_url.label,
            value: productImageUrl,
            order: propertyOrder++,
        };
        properties.push(productImageUrlProperty);
    }
    if (packageCode) {
        const packageNameProperty = {
            uid: guid(),
            name: defaultPropertyDefinitions.package_case_code.label,
            value: packageCode,
            order: propertyOrder++,
        };
        properties.push(packageNameProperty);
    }
    if (jlcPcbPartClass) {
        const jlcPcbPartClassProperty = {
            uid: guid(),
            name: defaultPropertyDefinitions.jlcPcbPartClass.label,
            value: jlcPcbPartClass,
            order: propertyOrder++,
        };
        properties.push(jlcPcbPartClassProperty);
    }
    return properties.reduce((acc, property) => {
        acc[property.uid] = property;
        return acc;
    }, {});
}
/**
 * Unified helper to apply import data to a document.
 * This is the single source of truth for setting description and properties on imported parts.
 * All importers (KiCad, Eagle, Altium) should use this function to ensure consistent behavior.
 *
 * This function:
 * 1. Sets the description (always, even if empty, to override the default placeholder)
 * 2. Builds and sets the standard properties (MPN, Manufacturer, Part Type, Datasheet URL, Designator Prefix)
 * 3. Merges any additional properties extracted from the source file
 *
 * @param documentData - The document data to modify (will be mutated)
 * @param importData - The import data containing property values
 * @param additionalProperties - Optional additional properties extracted from the source file
 */
export function applyImportDataToDocument(documentData, importData, additionalProperties) {
    // 1. Always set description to override the default placeholder text from createNewDocumentData
    // Even if description is empty, we want to show empty rather than the placeholder
    documentData.description = buildImportDescription(importData.description, importData.manufacturer, importData.mpn);
    // 2. Build standard properties using centralized utility (always includes 5 standard properties)
    const standardProperties = buildProperties({
        manufacturer: importData.manufacturer,
        mpn: importData.mpn,
        datasheetUrl: importData.datasheetUrl,
        designator: importData.designator,
        partType: importData.partType,
        description: importData.description,
        productDetailUrl: importData.productDetailUrl,
        productImageUrl: importData.productImageUrl,
        packageCode: importData.packageCode,
        lcscDpn: importData.lcscDpn,
        jlcPcbPartClass: importData.jlcPcbPartClass,
        value: importData.value,
    });
    // 3. Merge: standard properties first, then additional properties (additional can override if same name)
    const mergedProperties = { ...standardProperties };
    if (additionalProperties) {
        for (const [key, prop] of Object.entries(additionalProperties)) {
            // Check if this property already exists by name in standard properties
            const existingByName = Object.values(mergedProperties).find((p) => p.name === prop.name);
            if (existingByName) {
                // Update the existing property value if the additional one has a value
                if (prop.value) {
                    existingByName.value = prop.value;
                }
            }
            else {
                // Add new property that doesn't exist in standard properties
                mergedProperties[key] = prop;
            }
        }
    }
    // 4. Clear existing properties and set new ones to avoid duplicates
    // This ensures we don't end up with duplicate standard properties if the importer
    // or createNewDocumentData already added some properties
    documentData.properties = {};
    let orderIndex = 0;
    for (const [key, prop] of Object.entries(mergedProperties)) {
        documentData.properties[key] = {
            ...prop,
            order: orderIndex++,
        };
    }
}
export function drawInstructionsToSvg(drawInstructions, documentName) {
    const converter = new SymbolToSvgConverter(drawInstructions);
    const xTranslation = converter.symbolCenterToSymbolOrigin.dX;
    const yTranslation = converter.symbolCenterToSymbolOrigin.dY;
    const symbolAsset = {
        content: converter.getSvgString(),
        filename: `${documentName} Symbol.svg`,
    };
    return {
        symbolAsset: symbolAsset,
        symbolOffset: { x: xTranslation, y: yTranslation },
    };
}
// return random number
export function getRandomNumber() {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return Math.floor(Math.random() * 9999999) + 0;
}
export function buildTerminals(symbols, genesisTerminalPart, creator) {
    const terminalElements = [];
    const offset = { x: 0, y: 0 };
    for (const symbol of symbols) {
        for (const terminalData of symbol.terminalsData) {
            const newTerminalElement = ElementHelper.createNewElementData(genesisTerminalPart, 
            // Spread below to preserve `flip` and `rotation` properties
            { ...terminalData.diagramPosition, ...vec2.add(offset, terminalData.diagramPosition) }, creator.uid);
            newTerminalElement.label = terminalData.name;
            const terminalTypeProp = {
                uid: defaultPropertyDefinitions.pin_type.key,
                name: defaultPropertyDefinitions.pin_type.label,
                value: terminalData.type,
                order: Object.keys(newTerminalElement.properties).length + 1,
            };
            newTerminalElement.properties[defaultPropertyDefinitions.pin_type.key] = terminalTypeProp;
            const pinNumber = ElementHelper.getPinNumberProperty(newTerminalElement);
            if (pinNumber) {
                const pinNumberProp = {
                    ...pinNumber,
                    value: terminalData.number,
                    order: Object.keys(newTerminalElement.properties).length + 1,
                };
                newTerminalElement.properties[pinNumber.uid] = pinNumberProp;
            }
            const symbolPinPosition = ElementHelper.getSymbolPinPositionProperty(newTerminalElement);
            if (symbolPinPosition) {
                const symbolPinPositionProp = {
                    ...symbolPinPosition,
                    order: Object.keys(newTerminalElement.properties).length + 1,
                    value: {
                        type: "vector2",
                        data: terminalData.symbolPosition,
                    },
                };
                newTerminalElement.properties[symbolPinPosition.uid] = symbolPinPositionProp;
            }
            if (symbols.length > 1) {
                const symbolNameProp = {
                    uid: defaultPropertyDefinitions.subsymbol_designator_suffix.key,
                    name: defaultPropertyDefinitions.subsymbol_designator_suffix.label,
                    value: symbol.name ?? DEFAULT_SYMBOL_NAME,
                    order: Object.keys(newTerminalElement.properties).length + 1,
                };
                newTerminalElement.properties[defaultPropertyDefinitions.subsymbol_designator_suffix.key] =
                    symbolNameProp;
            }
            terminalElements.push(newTerminalElement);
        }
        const bounds = box2.fromPoints(symbol.terminalsData.map((t) => t.diagramPosition)) ?? box2.empty();
        offset.x += (bounds.max.x - bounds.min.x) * 2 + 40; // Symbol + symbol-width margin + minimal safety margin
    }
    return terminalElements;
}
export async function extractOptionalMetaData(assets, importData, scraperAPIKey) {
    if (assets.lcscAsset) {
        const lcscMetaData = JSON.parse(assets.lcscAsset.content);
        importData.manufacturer = lcscMetaData.result?.dataStr?.head.c_para?.Manufacturer || importData.manufacturer;
        importData.designator = importData.designator || lcscMetaData.result?.dataStr?.head.c_para?.pre;
        importData.lcscDpn = importData.lcscDpn || lcscMetaData.result?.dataStr?.head.c_para?.["Supplier Part"];
        if (lcscMetaData.result?.lcsc?.url &&
            (await checkWebsiteForString(lcscMetaData.result?.lcsc?.url, importData.lcscDpn))) {
            importData.productDetailUrl = lcscMetaData.result?.lcsc?.url;
        }
        importData.packageCode = importData.packageCode || lcscMetaData.result?.dataStr?.head.c_para?.package;
        importData.partType = lcscMetaData.result?.tags
            .filter((tag) => !tag.toLowerCase().includes("pre-ordered") && !tag.toLowerCase().includes("new arrival"))
            .join(", ");
        importData.mpn = lcscMetaData.result?.dataStr?.head.c_para?.["Manufacturer Part"] || importData.mpn;
        importData.jlcPcbPartClass = lcscMetaData.result?.dataStr?.head.c_para?.["JLCPCB Part Class"];
        importData.description = `${importData.partType ? importData.partType + "\n" : ""}${importData.mpn ? importData.mpn + "\n" : ""}${importData.packageCode ? importData.packageCode + "\n" : ""}LCSC Part Number: ${importData.lcscDpn}\n${importData.jlcPcbPartClass && "JLCPCB Part Class: " + importData.jlcPcbPartClass}\nManufactured by ${importData.manufacturer}`;
        importData.value = lcscMetaData.result?.dataStr?.head.c_para?.Value;
    }
    // Resolve the actual datasheet URL if the URL is a LCSC product page
    if (importData.datasheetUrl &&
        importData.datasheetUrl.toLowerCase().includes("lcsc") &&
        !importData.datasheetUrl.toLowerCase().includes(".pdf")) {
        importData.datasheetUrl =
            (await fetchDatasheetFromLcscProductPageUrl(importData.datasheetUrl, scraperAPIKey)) ||
                importData.datasheetUrl;
    }
    // Resolve the datasheet URL from the product detail URL if the URL is a LCSC product page
    if (!importData.datasheetUrl &&
        importData.productDetailUrl &&
        importData.productDetailUrl.toLowerCase().includes("lcsc")) {
        importData.datasheetUrl =
            (await fetchDatasheetFromLcscProductPageUrl(importData.productDetailUrl, scraperAPIKey)) || undefined;
    }
}
async function checkWebsiteForString(url, searchString) {
    try {
        const response = await fetch(url);
        // Check if the fetch was successful
        if (!response.ok) {
            return false;
        }
        // Get the text content of the response
        const pageContent = await response.text();
        // Check if the search string is present in the page content
        return pageContent.includes(searchString?.trim());
    }
    catch (error) {
        return false;
    }
}
//# sourceMappingURL=PartImportHelpers.js.map